import { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import { usePreferences } from '@atom/hooks/usePreferences';

import CreateWorkModalContext from '../CreateWorkModalContext';
import {
  addBlankTask,
  addTemplateTask,
  removeTask,
  renameTask,
} from '../createWorkModalUtilities';

import AddTaskMenu from './tasks/AddTaskMenu';
import TaskCard from './tasks/TaskCard';
import TasksEmptyState from './tasks/TasksEmptyState';
import TaskTemplatePicker from './tasks/TaskTemplatePicker';

import classes from './steps.module.css';

const TasksStep = () => {
  const {
    draft,
    updateDraftField,
    setCreateDisabled,
    taskTemplatePickerOpen,
    setTaskTemplatePickerOpen,
  } = useContext(CreateWorkModalContext);
  const tasks = draft.tasks || [];
  const workTemplateId = draft.workTemplate?.id || undefined;
  const schemaId = draft.asset?.schemaId || undefined;
  const disableTaskCreation =
    !!draft.workTemplate?.preferences?.disableTaskCreation;

  const preferences = usePreferences();
  const requireTaskType = R.pathOr(
    false,
    ['workOrders', 'requireTaskType'],
    preferences,
  );

  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    setCreateDisabled(taskTemplatePickerOpen || !R.isNil(editingId));
  }, [taskTemplatePickerOpen, editingId, setCreateDisabled]);

  if (taskTemplatePickerOpen) {
    return (
      <div className={classes.tasksStep}>
        <TaskTemplatePicker
          onClose={() => setTaskTemplatePickerOpen(false)}
          onConfirm={taskTemplate =>
            updateDraftField('tasks', addTemplateTask(tasks, taskTemplate))
          }
          workTemplateId={workTemplateId}
          schemaId={schemaId}
        />
      </div>
    );
  }

  return (
    <div className={classes.tasksStep}>
      <div className={classes.tasksHeader}>
        <div>{`${tasks.length} Task${tasks.length === 1 ? '' : 's'}`}</div>
        {!disableTaskCreation && (
          <AddTaskMenu
            requireTaskType={requireTaskType}
            onChooseTemplate={() => setTaskTemplatePickerOpen(true)}
            onAddBlank={() => updateDraftField('tasks', addBlankTask(tasks))}
          />
        )}
      </div>
      {tasks.length === 0 ? (
        <div className={classes.tasksEmptyContainer}>
          <TasksEmptyState
            requireTaskType={requireTaskType}
            onChooseTemplate={() => setTaskTemplatePickerOpen(true)}
            onAddBlank={() => updateDraftField('tasks', addBlankTask(tasks))}
          />
        </div>
      ) : (
        <div className={classes.tasksContent}>
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onRename={name =>
                updateDraftField('tasks', renameTask(tasks, task.id, name))
              }
              onDelete={() =>
                updateDraftField('tasks', removeTask(tasks, task.id))
              }
              onNameFocus={() => setEditingId(task.id)}
              onNameBlur={() => setEditingId(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TasksStep;
